import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import AuthShell, { buttonClass } from "../components/AuthShell";

/** Where the link in a verification email lands. */
const VerifyEmail = () => {
  const { t } = useI18n();
  const { user, updateUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState(token ? "working" : "invalid");
  // The link works once: StrictMode's second effect run must not spend it again
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    authAPI
      .verifyEmail(token)
      .then(async () => {
        setState("done");

        // The link may belong to someone other than whoever is signed in here, so
        // nothing is assumed: ask the server who this session is and what it knows.
        // The stored session is checked rather than `user`, which is still null
        // while the app is confirming it on a fresh page load.
        if (!localStorage.getItem("token")) return;

        try {
          const { data } = await authAPI.verify();
          if (data?.user) updateUser(data.user);
        } catch {
          // The banner just stays until the next load asks again
        }
      })
      .catch(() => setState("invalid"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthShell title={t("auth.verify.title")}>
      <p
        className={`p-4 rounded-lg text-sm border ${
          state === "done"
            ? "bg-green-50 border-green-200 text-green-800"
            : state === "invalid"
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-gray-50 border-gray-200 text-gray-600"
        }`}
        aria-live="polite"
      >
        {state === "done"
          ? t("auth.verify.done")
          : state === "invalid"
            ? t("auth.verify.invalid")
            : t("auth.verify.working")}
      </p>

      {state !== "working" && (
        <Link
          to={user || localStorage.getItem("token") ? "/admin" : "/login"}
          className={`${buttonClass} block text-center mt-6`}
        >
          {t("auth.verify.continue")}
        </Link>
      )}
    </AuthShell>
  );
};

export default VerifyEmail;
